import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Copy, FileText, Folder, FolderPlus, History, Pencil, Plus, Search, Trash2 } from "lucide-react";

import styles from "./Drive.module.scss";
import Spinner from "../../components/spinner";
import { useLanguage } from "../../context/languageContext";
import { Can, usePermissions } from "../../context/permissionContext";
import { copyText, driveApi, formatWhen } from "./driveApi";
import { copyFormatted, markdownToHtml } from "./markdown";
import ItemEditor from "./ItemEditor";
import HistoryModal from "./HistoryModal";

/**
 * Drive — the team's shared prompts and reference text, in folders.
 *
 * Two views of the same page. Listers (anything short of create/edit/delete)
 * see the prompt and a Copy button — nothing else: no dates, no names, no
 * history. The API sends them only title and text, so this is not merely
 * hidden. Owners and managers also get create/edit/delete, when each item was
 * added and last edited and by whom, and its full version history.
 *
 * The open folder and the search live in the URL (`?folder=`, `?q=`), so a
 * link to a folder can be shared and a reload lands in the same place.
 */

const SEARCH_DELAY = 300;

/** Long prompts collapse to this many lines until expanded. */
const PREVIEW_LINES = 8;

const Drive = () => {
  const { adminInfo } = useLanguage();
  const { can } = usePermissions();
  const manages = can("drive", "create") || can("drive", "edit") || can("drive", "delete");
  const api = useMemo(() => driveApi(adminInfo?.token), [adminInfo?.token]);

  const [params, setParams] = useSearchParams();
  const folderId = params.get("folder") || "";
  const query = params.get("q") || "";

  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchInput, setSearchInput] = useState(query);
  const [expanded, setExpanded] = useState({});

  const [editor, setEditor] = useState({ open: false, item: null });
  const [history, setHistory] = useState(null);

  // Takes every change at once: two calls in one tick would each start from
  // the same URL, and the second would undo the first.
  const setUrl = useCallback(
    (changes) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const loadFolders = useCallback(async () => {
    try {
      const rows = (await api.folders()) || [];
      setFolders(rows);
      return rows;
    } catch (error) {
      toast.error(error.message);
      return [];
    } finally {
      setLoadingFolders(false);
    }
  }, [api]);

  const loadItems = useCallback(async () => {
    if (!folderId && !query) {
      setItems([]);
      return;
    }
    setLoadingItems(true);
    try {
      // A search spans every folder; clearing it goes back to the open one.
      setItems((await api.items(query ? { q: query } : { folderId })) || []);
    } catch (error) {
      toast.error(error.message);
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [api, folderId, query]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    document.title = "Penspace - Drive";
    loadFolders();
  }, [adminInfo?.token, loadFolders]);

  // Land in the first folder when none is picked (or the linked one is gone).
  useEffect(() => {
    if (loadingFolders || query) return;
    if (folders.length && !folders.some((f) => f._id === folderId)) setUrl({ folder: folders[0]._id });
  }, [folders, folderId, query, loadingFolders, setUrl]);

  useEffect(() => {
    if (adminInfo?.token) loadItems();
  }, [adminInfo?.token, loadItems]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput.trim() !== query) setUrl({ q: searchInput.trim() });
    }, SEARCH_DELAY);
    return () => clearTimeout(id);
  }, [searchInput, query, setUrl]);

  const refresh = async () => {
    await Promise.all([loadFolders(), loadItems()]);
  };

  // ------------------------------------------------------------- folders --

  const newFolder = async () => {
    const name = window.prompt("Folder name");
    if (!name?.trim()) return;
    try {
      const folder = await api.createFolder({ name: name.trim() });
      toast.success(`"${folder.name}" created.`);
      await loadFolders();
      setSearchInput("");
      setUrl({ q: "", folder: folder._id });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renameFolder = async (folder) => {
    const name = window.prompt("Rename folder", folder.name);
    if (!name?.trim() || name.trim() === folder.name) return;
    try {
      await api.updateFolder(folder._id, { name: name.trim() });
      toast.success("Folder renamed.");
      await refresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete the folder "${folder.name}"?`)) return;
    try {
      await api.deleteFolder(folder._id);
      toast.success("Folder deleted.");
      if (folder._id === folderId) setUrl({ folder: "" });
      await loadFolders();
    } catch (error) {
      // The API refuses while the folder still holds items, and says so.
      toast.error(error.message);
    }
  };

  // --------------------------------------------------------------- items --

  const saveItem = async (body) => {
    try {
      if (editor.item) {
        await api.updateItem(editor.item._id, body);
        toast.success("Saved.");
      } else {
        await api.createItem(body);
        toast.success("Item added.");
      }
      setEditor({ open: false, item: null });
      await refresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.deleteItem(item._id);
      toast.success("Item deleted.");
      await refresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Copy = the Markdown source, for pasting into an LLM or a text box.
  const copy = async (item) => {
    try {
      await copyText(item.content);
      toast.success(`Copied "${item.title}" as Markdown.`);
    } catch {
      toast.error("Couldn't copy — open the item and copy the text by hand.");
    }
  };

  // Copy formatted = rich text, so a paste into Google Docs or Word keeps the
  // headings, bold and lists (a plain text box still gets the Markdown).
  const copyRich = async (item) => {
    try {
      await copyFormatted(item.content);
      toast.success(`Copied "${item.title}" with formatting.`);
    } catch {
      toast.error("This browser can't copy formatting here — use Copy for the Markdown.");
    }
  };

  const openFolder = folders.find((f) => f._id === folderId);

  if (loadingFolders) return <Spinner />;

  return (
    <React.Fragment>
      <div className="heading">
        <div>
          <h4 className="mb-0">AI Prompts</h4>
          <small className="text-muted">
            {manages
              ? "The team's prompts. Paste one in with New item; listers see only the prompt and a Copy button."
              : "The team's prompts. Copy from here rather than from chats, so everyone uses the latest version."}
          </small>
        </div>
      </div>

      <div className={styles.layout}>
        <aside className={styles.folders}>
          <div className={styles.foldersHead}>
            <span>Folders</span>
            <Can feature="drive" action="create">
              <button type="button" className={styles.iconButton} aria-label="New folder" onClick={newFolder}>
                <FolderPlus size={16} />
              </button>
            </Can>
          </div>

          {folders.length === 0 ? (
            <div className={styles.empty}>
              <p className="mb-2">No folders yet.</p>
              <Can feature="drive" action="create">
                <button type="button" className="btn btn-sm btn-dark" onClick={newFolder}>
                  <FolderPlus size={14} /> Create a folder
                </button>
              </Can>
            </div>
          ) : (
            <ul className={styles.folderList}>
              {folders.map((folder) => (
                <li key={folder._id}>
                  <div
                    className={`${styles.folderRow} ${!query && folder._id === folderId ? styles.active : ""}`}
                  >
                    <button
                      type="button"
                      className={styles.folderOpen}
                      onClick={() => {
                        setSearchInput("");
                        setUrl({ q: "", folder: folder._id });
                      }}
                    >
                      <Folder size={15} />
                      <span className={styles.folderName}>{folder.name}</span>
                      <span className={styles.folderCount}>{folder.itemCount}</span>
                    </button>
                    <Can feature="drive" action="edit">
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Rename ${folder.name}`}
                        onClick={() => renameFolder(folder)}
                      >
                        <Pencil size={13} />
                      </button>
                    </Can>
                    <Can feature="drive" action="delete">
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.danger}`}
                        aria-label={`Delete ${folder.name}`}
                        onClick={() => deleteFolder(folder)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </Can>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className={styles.main}>
          <div className={styles.toolbar}>
            <div className={styles.search}>
              <Search size={15} />
              <input
                type="search"
                placeholder="Search all of Drive…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Can feature="drive" action="create">
              <button
                type="button"
                className="btn btn-dark"
                disabled={folders.length === 0}
                title={folders.length === 0 ? "Create a folder first" : undefined}
                onClick={() => setEditor({ open: true, item: null })}
              >
                <Plus size={16} /> New item
              </button>
            </Can>
          </div>

          <h5 className={styles.sectionTitle}>
            {query ? `Results for "${query}"` : openFolder?.name || ""}
            {!loadingItems && (query || openFolder) ? <span> · {items.length}</span> : null}
          </h5>

          {loadingItems ? (
            <Spinner />
          ) : items.length === 0 ? (
            <p className={styles.empty}>
              {query ? "Nothing matches that." : openFolder ? "This folder is empty." : ""}
            </p>
          ) : (
            <ul className={styles.items}>
              {items.map((item) => {
                const lines = (item.content || "").split("\n");
                const long = lines.length > PREVIEW_LINES || item.content.length > 900;
                const open = expanded[item._id];
                const edited = item.version > 1 || item.updatedAt !== item.createdAt;
                return (
                  <li key={item._id} className={styles.item}>
                    <div className={styles.itemHead}>
                      <div className={styles.itemTitleWrap}>
                        <strong className={styles.itemTitle}>{item.title}</strong>
                        {query && <span className={styles.folderTag}>{item.folderName}</span>}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-dark"
                        title="Markdown — for ChatGPT, Claude or any text box"
                        onClick={() => copy(item)}
                      >
                        <Copy size={14} /> Copy
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title="Rich text — for Google Docs or Word"
                        onClick={() => copyRich(item)}
                      >
                        <FileText size={14} /> Copy formatted
                      </button>
                      {manages && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setHistory(item)}
                        >
                          <History size={14} /> v{item.version}
                        </button>
                      )}
                      <Can feature="drive" action="edit">
                        <button
                          type="button"
                          className={styles.iconButton}
                          aria-label={`Edit ${item.title}`}
                          onClick={() => setEditor({ open: true, item })}
                        >
                          <Pencil size={15} />
                        </button>
                      </Can>
                      <Can feature="drive" action="delete">
                        <button
                          type="button"
                          className={`${styles.iconButton} ${styles.danger}`}
                          aria-label={`Delete ${item.title}`}
                          onClick={() => deleteItem(item)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </Can>
                    </div>

                    {manages && (
                      <div className={styles.meta}>
                      Added {formatWhen(item.createdAt)}
                      {item.createdBy?.adminName ? ` by ${item.createdBy.adminName}` : ""}
                      {edited && (
                        <>
                          {" · "}Edited {formatWhen(item.updatedAt)}
                          {item.updatedBy?.adminName ? ` by ${item.updatedBy.adminName}` : ""}
                        </>
                      )}
                    </div>
                    )}

                    <div
                      className={`${styles.markdown} ${styles.content} ${long && !open ? styles.clamped : ""}`}
                      // Sanitised in markdownToHtml.
                      dangerouslySetInnerHTML={{ __html: markdownToHtml(item.content) || "<p>(empty)</p>" }}
                    />
                    {long && (
                      <button
                        type="button"
                        className={styles.more}
                        onClick={() => setExpanded((e) => ({ ...e, [item._id]: !open }))}
                      >
                        {open ? "Show less" : "Show all"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <ItemEditor
        isOpen={editor.open}
        item={editor.item}
        folders={folders}
        defaultFolderId={folderId}
        onSave={saveItem}
        onClose={() => setEditor({ open: false, item: null })}
      />

      <HistoryModal
        isOpen={Boolean(history)}
        item={history}
        api={api}
        canRestore={can("drive", "edit")}
        onRestored={async () => {
          setHistory(null);
          await refresh();
        }}
        onClose={() => setHistory(null)}
      />
    </React.Fragment>
  );
};

export default Drive;
